import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, MessageSquare, Phone, Users, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { DirectMessageDialog } from "@/components/DirectMessageDialog";
import { SprintInvitationAcceptDialog } from "@/components/sprint/SprintInvitationAcceptDialog";
import { CallInvitationAcceptDialog } from "@/components/call/CallInvitationAcceptDialog";
import { ChannelInvitationAcceptDialog } from "@/components/channel/ChannelInvitationAcceptDialog";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string | null;
  action_url: string | null;
  read: boolean;
  created_at: string;
  metadata?: any;
}

// Helper function to get notification background and text styles based on type
const getNotificationStyles = (type: string) => {
  switch (type) {
    case 'direct_message':
      return 'bg-white text-black border border-gray-200';
    case 'network_invitation':
      return 'bg-blue-100 text-black';
    case 'channel_invitation':
    case 'channel_invitation_accepted':
    case 'channel_invitation_declined':
      return 'bg-green-100 text-black';
    case 'sprint_invitation':
      return 'bg-red-100 text-black';
    case 'call':
    case 'call_invitation':
      return 'bg-gray-900 text-white';
    default:
      return 'bg-white text-black border border-gray-200';
  }
};

// Helper to get icon color based on notification type (white for dark backgrounds)
const getIconColor = (type: string) => {
  if (type === 'call' || type === 'call_invitation') {
    return 'text-white';
  }
  return '';
};

export const NotificationBell = ({ compact = false }: { compact?: boolean }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [sentNotifications, setSentNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dmDialogOpen, setDmDialogOpen] = useState(false);
  const [dmRecipientId, setDmRecipientId] = useState<string | undefined>();
  
  // Sprint invitation dialog state
  const [sprintInvitationDialogOpen, setSprintInvitationDialogOpen] = useState(false);
  const [selectedSprintInvitation, setSelectedSprintInvitation] = useState<{
    notificationId: string;
    sprintId: string;
    sprintObjective: string;
    inviterName: string;
    customMessage?: string;
    invitationId: string;
  } | null>(null);

  // Call invitation dialog state
  const [callInvitationDialogOpen, setCallInvitationDialogOpen] = useState(false);
  const [selectedCallInvitation, setSelectedCallInvitation] = useState<{
    notificationId: string;
    callId: string;
    callTitle: string;
    callDescription?: string;
    inviterName: string;
    bountyAmount?: number;
    callType?: string;
  } | null>(null);

  // Channel invitation dialog state
  const [channelInvitationDialogOpen, setChannelInvitationDialogOpen] = useState(false);
  const [selectedChannelInvitation, setSelectedChannelInvitation] = useState<{
    notificationId: string;
    invitationId: string;
    channelId: string;
    channelName: string;
    inviterName: string;
  } | null>(null);

  useEffect(() => {
    if (!user) return;

    loadNotifications();

    // Subscribe to real-time notifications
    const receivedChannel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    const sentChannel = supabase
      .channel('sent-notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sent_notifications',
          filter: `sender_id=eq.${user.id}`
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(receivedChannel);
      supabase.removeChannel(sentChannel);
    };
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;

    // Load received notifications (no limit - show full history)
    const { data: receivedData } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (receivedData) {
      setNotifications(receivedData);
      setUnreadCount(receivedData.filter(n => !n.read).length);
    }

    // Load sent notifications (no limit - show full history)
    const { data: sentData } = await supabase
      .from('sent_notifications')
      .select('*')
      .eq('sender_id', user.id)
      .order('created_at', { ascending: false });

    if (sentData) {
      setSentNotifications(sentData);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Handle sprint invitation with interactive response
    if (notification.type === 'sprint_invitation' && notification.metadata?.requires_response) {
      setSelectedSprintInvitation({
        notificationId: notification.id,
        sprintId: notification.metadata.sprint_id,
        sprintObjective: notification.metadata.sprint_objective,
        inviterName: notification.metadata.inviter_name,
        customMessage: notification.metadata.custom_message,
        invitationId: notification.metadata.invitation_id,
      });
      setSprintInvitationDialogOpen(true);
      return;
    }

    // Handle call invitation with interactive response
    if (notification.type === 'call_invitation' && notification.metadata?.requires_response) {
      setSelectedCallInvitation({
        notificationId: notification.id,
        callId: notification.metadata.call_id,
        callTitle: notification.metadata.call_title,
        callDescription: notification.metadata.call_description,
        inviterName: notification.metadata.inviter_name,
        bountyAmount: notification.metadata.bounty_amount,
        callType: notification.metadata.call_type,
      });
      setCallInvitationDialogOpen(true);
      return;
    }

    // Handle channel invitation with interactive response
    if (notification.type === 'channel_invitation' && notification.metadata?.requires_response) {
      setSelectedChannelInvitation({
        notificationId: notification.id,
        invitationId: notification.metadata.invitation_id,
        channelId: notification.metadata.channel_id,
        channelName: notification.metadata.channel_name,
        inviterName: notification.metadata.inviter_name,
      });
      setChannelInvitationDialogOpen(true);
      return;
    }

    // Mark as read
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notification.id);

    // Handle direct message notifications by opening DM dialog
    if (notification.type === 'direct_message') {
      const metadata = notification.metadata as any;
      if (metadata?.sender_id) {
        setDmRecipientId(metadata.sender_id);
        setDmDialogOpen(true);
      }
    } else if (notification.action_url) {
      // Navigate to action URL for other notification types
      navigate(notification.action_url);
    }

    loadNotifications();
  };

  const markAllAsRead = async () => {
    if (!user) return;

    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);

    loadNotifications();
  };

  const getNotificationIcon = (type: string) => {
    const iconColor = getIconColor(type);
    switch (type) {
      case 'direct_message':
        return <MessageSquare className={`h-4 w-4 mr-2 flex-shrink-0 ${iconColor}`} />;
      case 'call':
      case 'call_invitation':
        return <Phone className={`h-4 w-4 mr-2 flex-shrink-0 ${iconColor}`} />;
      case 'sprint_invitation':
        return <Users className={`h-4 w-4 mr-2 flex-shrink-0 ${iconColor}`} />;
      case 'channel_invitation':
      case 'channel_invitation_accepted':
      case 'channel_invitation_declined':
        return <Hash className={`h-4 w-4 mr-2 flex-shrink-0 ${iconColor}`} />;
      default:
        return <Bell className={`h-4 w-4 mr-2 flex-shrink-0 ${iconColor}`} />;
    }
  };

  // Filter notifications based on type filter
  const filterByType = (notifs: Notification[]) => {
    if (typeFilter === 'all') return notifs;
    return notifs.filter(n => {
      if (typeFilter === 'message') return n.type === 'direct_message';
      if (typeFilter === 'channel') return n.type.includes('channel');
      if (typeFilter === 'sprint') return n.type === 'sprint_invitation';
      if (typeFilter === 'call') return n.type.includes('call');
      return true;
    });
  };

  const filteredNotifications = filterByType(notifications);
  const filteredSentNotifications = filterByType(sentNotifications);

  if (!user) return null;

  return (
    <TooltipProvider>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant={compact ? "default" : "active"} size="icon" className={compact ? "relative h-7 w-7 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" : "relative"}>
                <Bell className={compact ? "h-3.5 w-3.5" : "h-5 w-5"} />
                {unreadCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className={compact ? "absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-[10px]" : "absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"}
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Notifications</p>
          </TooltipContent>
        </Tooltip>
      <DropdownMenuContent align="end" className="w-[600px]">
        <div className="flex items-center justify-between p-2 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-auto p-1 text-xs"
            >
              Mark all read
            </Button>
          )}
        </div>
        {/* Type filter buttons */}
        <div className="flex gap-1 p-2 border-b bg-muted/50 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            { key: 'message', label: 'Messages' },
            { key: 'channel', label: 'Channel' },
            { key: 'sprint', label: 'Sprint' },
            { key: 'call', label: 'Call' },
          ].map(filter => (
            <Button
              key={filter.key}
              variant={typeFilter === filter.key ? 'default' : 'outline'}
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTypeFilter(filter.key);
              }}
              className="h-6 text-xs px-2"
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <div className="flex h-[360px]">
          {/* Left half - Received */}
          <div className="w-1/2 border-r">
            <h4 className="text-xs font-semibold p-2 border-b bg-muted">Received</h4>
            <ScrollArea className="h-[320px]">
              {filteredNotifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No received notifications
                </div>
              ) : (
                filteredNotifications.map((notification) => (
                  <HoverCard key={notification.id} openDelay={300}>
                    <HoverCardTrigger asChild>
                      <DropdownMenuItem
                        className={`p-3 cursor-pointer rounded-md m-1 ${getNotificationStyles(notification.type)}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start w-full">
                          {getNotificationIcon(notification.type)}
                          <div className="space-y-1 flex-1">
                            <p className={`text-sm ${!notification.read ? 'font-bold' : 'font-medium'} ${notification.type === 'call' || notification.type === 'call_invitation' ? 'text-white' : (!notification.read ? 'text-green-600' : 'text-black')}`}>
                              {notification.title}
                            </p>
                            {notification.message && (
                              <p className={`text-xs line-clamp-2 ${notification.type === 'call' || notification.type === 'call_invitation' ? 'text-gray-300' : 'text-gray-600'}`}>
                                {notification.message}
                              </p>
                            )}
                            <p className={`text-xs ${notification.type === 'call' || notification.type === 'call_invitation' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    </HoverCardTrigger>
                    <HoverCardContent side="left" align="start" className="w-80 z-[10000]">
                      <div className="space-y-2">
                        <p className="font-semibold text-sm">{notification.title}</p>
                        {notification.message && (
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ))
              )}
            </ScrollArea>
          </div>
          
          {/* Right half - Sent */}
          <div className="w-1/2">
            <h4 className="text-xs font-semibold p-2 border-b bg-muted">Sent</h4>
            <ScrollArea className="h-[320px]">
              {filteredSentNotifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No sent notifications
                </div>
              ) : (
                filteredSentNotifications.map((notification) => (
                  <HoverCard key={notification.id} openDelay={300}>
                    <HoverCardTrigger asChild>
                      <DropdownMenuItem className={`p-3 rounded-md m-1 ${getNotificationStyles(notification.type)}`}>
                        <div className="flex items-start w-full">
                          {getNotificationIcon(notification.type)}
                          <div className="space-y-1 flex-1">
                            <p className={`text-sm font-medium ${notification.type === 'call' || notification.type === 'call_invitation' ? 'text-white' : 'text-black'}`}>{notification.title}</p>
                            {notification.message && (
                              <p className={`text-xs line-clamp-2 ${notification.type === 'call' || notification.type === 'call_invitation' ? 'text-gray-300' : 'text-gray-600'}`}>
                                {notification.message}
                              </p>
                            )}
                            <p className={`text-xs ${notification.type === 'call' || notification.type === 'call_invitation' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    </HoverCardTrigger>
                    <HoverCardContent side="right" align="start" className="w-80 z-[10000]" sideOffset={5}>
                      <div className="space-y-2">
                        <p className="font-semibold text-sm">{notification.title}</p>
                        {notification.message && (
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ))
              )}
            </ScrollArea>
          </div>
        </div>
      </DropdownMenuContent>
      </DropdownMenu>
      
      <DirectMessageDialog
        open={dmDialogOpen} 
        onOpenChange={setDmDialogOpen} 
        preselectedRecipientId={dmRecipientId} 
      />
      
      {selectedSprintInvitation && (
        <SprintInvitationAcceptDialog
          open={sprintInvitationDialogOpen}
          onOpenChange={(open) => {
            setSprintInvitationDialogOpen(open);
            if (!open) {
              setSelectedSprintInvitation(null);
              loadNotifications();
            }
          }}
          notificationId={selectedSprintInvitation.notificationId}
          sprintId={selectedSprintInvitation.sprintId}
          sprintObjective={selectedSprintInvitation.sprintObjective}
          inviterName={selectedSprintInvitation.inviterName}
          customMessage={selectedSprintInvitation.customMessage}
          invitationId={selectedSprintInvitation.invitationId}
        />
      )}

      {selectedCallInvitation && (
        <CallInvitationAcceptDialog
          open={callInvitationDialogOpen}
          onOpenChange={(open) => {
            setCallInvitationDialogOpen(open);
            if (!open) {
              setSelectedCallInvitation(null);
              loadNotifications();
            }
          }}
          notificationId={selectedCallInvitation.notificationId}
          callId={selectedCallInvitation.callId}
          callTitle={selectedCallInvitation.callTitle}
          callDescription={selectedCallInvitation.callDescription}
          inviterName={selectedCallInvitation.inviterName}
          bountyAmount={selectedCallInvitation.bountyAmount}
          callType={selectedCallInvitation.callType}
        />
      )}

      {selectedChannelInvitation && (
        <ChannelInvitationAcceptDialog
          open={channelInvitationDialogOpen}
          onOpenChange={(open) => {
            setChannelInvitationDialogOpen(open);
            if (!open) {
              setSelectedChannelInvitation(null);
              loadNotifications();
            }
          }}
          notificationId={selectedChannelInvitation.notificationId}
          invitationId={selectedChannelInvitation.invitationId}
          channelId={selectedChannelInvitation.channelId}
          channelName={selectedChannelInvitation.channelName}
          inviterName={selectedChannelInvitation.inviterName}
        />
      )}
    </TooltipProvider>
  );
};